import { toast } from "react-toastify";

import store from "../../../redux/store";
import { getBillingList } from "../../../redux/actions/master/billingActions";
import {
  editBillingDataByIdApi,
  getBillingBulkRunByIdApi,
  getBillingListApi,
} from "../../../redux/sagas/requests/billing/billingRequests";
import { getPincodeByPincodeApi } from "../../../redux/sagas/requests/master/pincodeRequest";
import { getCustomerPricingApi } from "../../../redux/sagas/requests/master/customerRateRequests";
import { getCustomerValueAddedServicesApi } from "../../../redux/sagas/requests/master/customerVasRequests";
import { getAwbByIdApi } from "../../../redux/sagas/requests/operations/awbRequests";
import { IBillingBulkRun, IBillingListItem, IBillingUpdate, IGenerateBilling } from "../../../types/master/billingInterface";
import { ICustomerPricingRate } from "../../../types/master/customerRateInterface";
import { ICustomerValueAddedServiceCharge } from "../../../types/master/customerVasInterface";
import { DEFAULT_CATEGORIES, IVasCharge } from "../valueAddedServices/ValueAddedServices";

/*
 * Prices a bill from its customer's own sheets: the rate card (GET /customer-pricing/customer/:id)
 * and the Value Added Services sheet (GET /customer/value-added-services/:id).
 *
 * The backend still creates bills; once a Generate Billing run finishes, the bills that run created
 * are repriced here (basic freight, docket, fuel surcharge, plus the insurance, ODA, To Pay, COD and green tax their AWB calls for)
 * and saved through the normal bill update.
 * Bills that already existed are never touched. Other charges are added on the bill page with ＋.
 */

export type VasColumn =
  | "docket_charge"
  | "fuel_surcharge"
  | "owner_risk"
  | "carrier_risk"
  | "oda_1"
  | "oda_2"
  | "oda_3"
  | "to_pay_charge"
  | "sunday_holiday_delivery_charges"
  | "cod_charge"
  | "hard_copy_pod"
  | "demurrage_charges"
  | "floor_delivery"
  | "package_handling_charge"
  | "appointment"
  | "reattempt_charges"
  | "crane_charge"
  | "labour_charge";

// every VAS column on a bill, with the label the bill screens use
export const BILL_VAS_COLUMNS: Array<{ column: VasColumn; label: string }> = [
  { column: "docket_charge", label: "Docket Charge" },
  { column: "fuel_surcharge", label: "Fuel Surcharge" },
  { column: "oda_1", label: "ODA 1" },
  { column: "oda_2", label: "ODA 2" },
  { column: "oda_3", label: "ODA 3" },
  { column: "owner_risk", label: "Owner Risk" },
  { column: "carrier_risk", label: "Carrier Risk" },
  { column: "cod_charge", label: "COD Charge" },
  { column: "to_pay_charge", label: "To Pay Charge" },
  { column: "hard_copy_pod", label: "Hard Copy POD" },
  { column: "sunday_holiday_delivery_charges", label: "Sunday/Holiday Delivery" },
  { column: "floor_delivery", label: "Floor Delivery" },
  { column: "package_handling_charge", label: "Package Handling Charge" },
  { column: "demurrage_charges", label: "Demurrage Charges" },
  { column: "appointment", label: "Appointment" },
  { column: "reattempt_charges", label: "Re-attempt Delivery" },
  { column: "crane_charge", label: "Crane / Pulley / Equipment" },
  { column: "labour_charge", label: "Warai / Union / Mathadi" },
];

// sheet codes that have their own column on the bill; every other priced code goes to additional charges
const COLUMN_BY_CODE: Record<string, VasColumn> = {
  BASIC_DOCKET_CHARGE: "docket_charge",
  FUEL_SURCHARGE: "fuel_surcharge",
  OWNER_RISK: "owner_risk",
  CARRIER_RISK: "carrier_risk",
  ODA_LEVEL_1: "oda_1",
  ODA_LEVEL_2: "oda_2",
  ODA_LEVEL_3: "oda_3",
  TO_PAY_CHARGES: "to_pay_charge",
  SUNDAY_HOLIDAY_DELIVERY: "sunday_holiday_delivery_charges",
  CASH_ON_DELIVERY: "cod_charge",
  HARD_COPY_POD: "hard_copy_pod",
  WAREHOUSE_DEMURRAGE: "demurrage_charges",
  FLOOR_DELIVERY: "floor_delivery",
  HEAVY_SHIPMENT_HANDLING: "package_handling_charge",
  REATTEMPT_DELIVERY: "reattempt_charges",
  CRANE_PULLEY_EQUIPMENT: "crane_charge",
  WARAI_UNION_MATHADI: "labour_charge",
};

// codes that shape freight instead of being a charge, or that need data a bill doesn't carry
const FREIGHT_RULE_CODES = ["MINIMUM_FREIGHT", "MINIMUM_CHARGEABLE_WEIGHT", "TOTAL_WEIGHT_ROUNDING", "FREIGHT_ROUNDING"];
// CFT is already in the chargeable weight, escalation needs the diesel price change, demurrage needs the storage days
const NOT_PRICEABLE_CODES = ["CFT_CONVERSION_FACTOR", "FUEL_ESCALATION", "WAREHOUSE_DEMURRAGE"];

const SHEET_CHARGES: IVasCharge[] = DEFAULT_CATEGORIES.flatMap((category) => category.charges);

export type CustomerPricing = {
  rates: ICustomerPricingRate[];
  vas: Map<string, { value: number; minimum: number | null }>;
};

export type VasLine = { code?: string; label: string; amount: number; minApplied?: boolean };

// what the AWB decides about a bill: which charges apply, and the invoice value the percentage ones are charged on.
// deliveryStatus is the destination pincode's delivery status; null when it couldn't be looked up
export type AwbCharges = {
  insuranceType: string;
  paymentMode: string;
  destinationPincode: string;
  deliveryStatus: string | null;
  invoiceValue: number;
};

export type PricedBill = {
  update: IBillingUpdate;
  lines: VasLine[];
  rateSource: "customer" | "master" | "unchanged";
};

const round2 = (value: number) => Math.round(value * 100) / 100;
const toNumber = (value: unknown) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};
const sameName = (a?: string | null, b?: string | null) => (a ?? "").trim().toLowerCase() === (b ?? "").trim().toLowerCase();

const pricingCache = new Map<number, Promise<CustomerPricing>>();

export const loadCustomerPricing = (customerId: number) => {
  if (!pricingCache.has(customerId)) {
    pricingCache.set(
      customerId,
      Promise.all([getCustomerPricingApi(customerId), getCustomerValueAddedServicesApi(customerId)])
        .then(([rateResponse, vasResponse]) => {
          const charges: ICustomerValueAddedServiceCharge[] = vasResponse.data?.data?.charges ?? [];
          return {
            rates: rateResponse.data?.data?.rates ?? [],
            // only charges the customer has a value for
            vas: new Map(
              charges
                .filter((charge) => charge.value !== null && charge.value !== undefined && `${charge.value}` !== "")
                .map((charge) => [charge.code, { value: Number(charge.value), minimum: charge.minimum === null ? null : Number(charge.minimum) }])
            ),
          };
        })
        .catch((error) => {
          pricingCache.delete(customerId);
          throw error;
        })
    );
  }
  return pricingCache.get(customerId)!;
};

const deliveryStatusCache = new Map<string, Promise<string | null>>();

// the pincode master's delivery status (NORMAL, ODA-1…); null when it can't be looked up
export const loadDeliveryStatus = (pincode: unknown) => {
  const key = String(pincode ?? "").trim();
  if (!key) return Promise.resolve(null);
  if (!deliveryStatusCache.has(key)) {
    deliveryStatusCache.set(
      key,
      getPincodeByPincodeApi({ pincode: key })
        .then(({ data }) => {
          const status: string | null = data?.data?.delivery ?? null;
          if (status === null) deliveryStatusCache.delete(key);
          return status;
        })
        .catch((error) => {
          console.error(error);
          deliveryStatusCache.delete(key);
          return null;
        })
    );
  }
  return deliveryStatusCache.get(key)!;
};

// a rate card or pincode change should apply to the next bills, so the caches only live for one repricing run
export const clearCustomerPricingCache = () => {
  pricingCache.clear();
  deliveryStatusCache.clear();
};

const findLaneRate = (rates: ICustomerPricingRate[], bill: IBillingListItem) => {
  const lane = (origin: string, destination: string) =>
    rates.find(
      (rate) =>
        sameName(rate.origin_zone?.zone_name, origin) &&
        sameName(rate.destination_zone?.zone_name, destination) &&
        sameName(rate.service_mode, bill.service_mode)
    );
  // the backend also falls back to the reverse lane
  const match = lane(bill.origin_zone, bill.destination_zone) ?? lane(bill.destination_zone, bill.origin_zone);
  if (!match) return null;
  if (match.rate !== null && match.rate !== undefined && `${match.rate}` !== "") return { rate: Number(match.rate), source: "customer" as const };
  if (match.master_rate !== null && match.master_rate !== undefined) return { rate: Number(match.master_rate), source: "master" as const };
  return null;
};

const sheetCharge = (code: string) => SHEET_CHARGES.find((charge) => charge.code === code);

// the charges the AWB decides, so they are fixed lines on the bill and never picked with ＋
const ODA_CODES = ["ODA_LEVEL_1", "ODA_LEVEL_2", "ODA_LEVEL_3"];
const AWB_DECIDED_CODES = ["OWNER_RISK", "CARRIER_RISK", "GREEN_TAX_DELHI", "TO_PAY_CHARGES", "CASH_ON_DELIVERY", ...ODA_CODES];

// if the delivery pincode couldn't be checked, ODA stays addable by hand
export const awbDecidedCodes = (awb: AwbCharges) =>
  awb.deliveryStatus === null ? AWB_DECIDED_CODES.filter((code) => !ODA_CODES.includes(code)) : AWB_DECIDED_CODES;

// green tax has no column on a bill; it is saved in additional charges under this name
export const GREEN_TAX_NAME = sheetCharge("GREEN_TAX_DELHI")?.name ?? "Green Tax (Delhi)";

export const isGreenTaxCharge = (chargeName?: string | null) => sameName(chargeName, GREEN_TAX_NAME);

// every Delhi pincode starts with 110; Gurugram (122) and Noida (201) don't
export const isDelhiPincode = (pincode: unknown) => /^110\d{3}$/.test(String(pincode ?? "").trim());

const CODE_BY_INSURANCE: Record<string, string> = { OWNER_RISK: "OWNER_RISK", CARRIER_RISK: "CARRIER_RISK" };
const CODE_BY_PAYMENT_MODE: Record<string, string> = { TO_PAY: "TO_PAY_CHARGES", COD: "CASH_ON_DELIVERY" };
const CODE_BY_DELIVERY_STATUS: Record<string, string> = { "ODA-1": "ODA_LEVEL_1", "ODA-2": "ODA_LEVEL_2", "ODA-3": "ODA_LEVEL_3" };

// the bill columns this AWB always charges, with the sheet code that prices each
export const awbChargeColumns = (awb: AwbCharges) =>
  [CODE_BY_INSURANCE[awb.insuranceType], CODE_BY_DELIVERY_STATUS[awb.deliveryStatus ?? ""], CODE_BY_PAYMENT_MODE[awb.paymentMode]]
    .filter(Boolean)
    .map((code) => ({ code, column: COLUMN_BY_CODE[code] }));

export const toAwbCharges = (awb: any, deliveryStatus: string | null): AwbCharges => ({
  insuranceType: awb?.insurance_type ?? "",
  paymentMode: awb?.payment_mode ?? "",
  destinationPincode: String(awb?.destination_pincode ?? ""),
  deliveryStatus,
  invoiceValue: (awb?.invoice_details ?? []).reduce((sum: number, invoice: { invoice_value?: unknown }) => sum + toNumber(invoice.invoice_value), 0),
});

// One charge from the customer's VAS sheet: value × weight, % of freight or invoice value, or a fixed amount, at least their minimum.
// null when the customer has no value for it.
export const priceSheetCharge = (code: string, pricing: CustomerPricing, weight: number, freight: number, invoiceValue = 0): VasLine | null => {
  const charge = sheetCharge(code);
  const entry = pricing.vas.get(code);
  if (!charge || !entry) return null;

  let amount: number;
  let minApplied = false;
  if (charge.calculationType === "Percentage") {
    if (charge.appliedOn === "Base Freight") amount = (freight * entry.value) / 100;
    else if (invoiceValue > 0) amount = (invoiceValue * entry.value) / 100;
    else {
      // no invoice value on the AWB, so the customer's minimum is what can be charged
      amount = 0;
      minApplied = true;
    }
  } else if (charge.calculationType === "KGS") amount = weight * entry.value;
  else amount = entry.value; // Fixed, and the agreed amount for "as per actual / approval" charges

  if (entry.minimum !== null && amount < entry.minimum) {
    amount = entry.minimum;
    minApplied = true;
  }
  return { code, label: charge.name, amount: round2(amount), minApplied };
};

// The sheet code behind a bill column or an additional charge name, to explain the charge on the bill
export const sheetCodeFor = (columnOrName: string) =>
  Object.keys(COLUMN_BY_CODE).find((code) => COLUMN_BY_CODE[code] === columnOrName) ??
  SHEET_CHARGES.find((charge) => sameName(charge.name, columnOrName))?.code;

// How the customer's sheet prices a charge, e.g. "₹3/kg × 12 kg", "12% of freight"; empty when they have no value for it
export const describeSheetCharge = (code: string | undefined, pricing: CustomerPricing | null, weight: number) => {
  const charge = code ? sheetCharge(code) : undefined;
  const entry = code ? pricing?.vas.get(code) : undefined;
  if (!charge || !entry) return "";
  const minimum = entry.minimum !== null && entry.minimum > 0 ? ` · min ₹${entry.minimum}` : "";
  if (charge.calculationType === "Percentage")
    return `${entry.value}% of ${charge.appliedOn === "Base Freight" ? "freight" : "invoice value"}${minimum}`;
  if (charge.calculationType === "KGS") return `₹${entry.value}/kg × ${weight} kg${minimum}`;
  return `Fixed ₹${entry.value}${minimum}`;
};

// weight after the customer's minimum and rounding
export const billableWeight = (chargeableWeight: unknown, pricing: CustomerPricing) => {
  let weight = toNumber(chargeableWeight);
  const minimum = pricing.vas.get("MINIMUM_CHARGEABLE_WEIGHT");
  if (minimum) weight = Math.max(weight, minimum.value);
  if (pricing.vas.has("TOTAL_WEIGHT_ROUNDING")) weight = Math.ceil(weight);
  return weight;
};

// Reprices a bill from the customer's sheets: basic freight, docket and fuel surcharge, plus what its AWB calls for: insurance,
// ODA for the delivery pincode, To Pay or COD by payment mode, and green tax for a Delhi destination.
// Every other charge on the bill is kept as it is: the delivery charges the backend generated (Sunday / holiday, hard copy POD,
// appointment, re-attempt, special delivery, crane, labour) and anything added by hand with ＋.
export const priceBill = (bill: IBillingListItem, pricing: CustomerPricing, awb: AwbCharges): PricedBill => {
  const weight = billableWeight(bill.chargeable_weight, pricing);

  // freight: the customer's lane rate × weight, at least their minimum freight
  const lane = findLaneRate(pricing.rates, bill);
  const ratePerKg = lane ? lane.rate : toNumber(bill.rate_per_kg);
  let freight = ratePerKg * weight;
  const minimumFreight = pricing.vas.get("MINIMUM_FREIGHT");
  if (minimumFreight) freight = Math.max(freight, minimumFreight.value);
  freight = pricing.vas.has("FREIGHT_ROUNDING") ? Math.ceil(freight) : round2(freight);

  const docket = priceSheetCharge("BASIC_DOCKET_CHARGE", pricing, weight, freight);
  const fuel = priceSheetCharge("FUEL_SURCHARGE", pricing, weight, freight);

  // every charge starts as the bill has it (what the backend generated, or what someone entered: Sunday / holiday, POD,
// appointment, re-attempt, crane, labour…);
  // only the charges priced here are replaced: docket, fuel, and the ones the AWB decides (insurance, To Pay / COD, ODA)
  const columns = Object.fromEntries(BILL_VAS_COLUMNS.map(({ column }) => [column, toNumber(bill[column])])) as Record<VasColumn, number>;
  const decided: VasColumn[] = ["owner_risk", "carrier_risk", "to_pay_charge", "cod_charge"];
  if (awb.deliveryStatus !== null) decided.push("oda_1", "oda_2", "oda_3");
  decided.forEach((column) => {
    columns[column] = 0;
  });
  columns.docket_charge = docket?.amount ?? 0;
  columns.fuel_surcharge = fuel?.amount ?? 0;

  const awbLines = awbChargeColumns(awb).map(({ code, column }) => {
    const line = priceSheetCharge(code, pricing, weight, freight, awb.invoiceValue);
    columns[column] = line?.amount ?? 0;
    return line;
  });

  const greenTax = isDelhiPincode(awb.destinationPincode) ? priceSheetCharge("GREEN_TAX_DELHI", pricing, weight, freight) : null;

  // the bill's other charges stay as they are: delivery charges the backend added (re-attempt, special delivery,
  // crane, labour…) and anything typed in by hand; only green tax is priced again here
  const keptExtras = (bill.additional_charges ?? []).filter((extra) => !isGreenTaxCharge(extra.charge_name));

  const update: IBillingUpdate = {
    ...columns,
    rate_per_kg: ratePerKg,
    basic_freight: freight,
    payment_status: bill.payment_status,
    invoice_number: bill.invoice_number,
    remarks: bill.remarks,
    additional_charges: [
      ...keptExtras.map((extra) => ({ charge_name: extra.charge_name, amount: toNumber(extra.amount), proof_url: extra.proof_url ?? null })),
      ...(greenTax ? [{ charge_name: GREEN_TAX_NAME, amount: greenTax.amount }] : []),
    ],
  };

  return {
    update,
    lines: [docket, fuel, ...awbLines, greenTax].filter((line): line is VasLine => Boolean(line)),
    rateSource: lane ? lane.source : "unchanged",
  };
};

/* ---------- delivery charges decided on the AWB's Order Summary ---------- */

export type SpecialDeliveryType = "" | "APPOINTMENT" | "MALL" | "GOVT" | "CSD";

export const SPECIAL_DELIVERY_OPTIONS: Array<{ value: SpecialDeliveryType; label: string }> = [
  { value: "", label: "None" },
  { value: "APPOINTMENT", label: "Appointment" },
  { value: "MALL", label: "Mall" },
  { value: "GOVT", label: "Govt" },
  { value: "CSD", label: "CSD" },
];

// storage is free for the first 6 days at destination; re-attempts are charged after 2 failed attempts
export const FREE_STORAGE_DAYS = 6;
export const FREE_DELIVERY_ATTEMPTS = 2;

export type DeliveryChargeInputs = {
  specialDelivery: SpecialDeliveryType;
  isSundayOrHoliday: boolean;
  deliveryAttempts: number;
  hardCopyPod: boolean;
  storageDays: number;
  // null when not applicable; otherwise the amount as per actual
  craneAmount: number | null;
  labourAmount: number | null;
};

const chargeName = (code: string, fallback: string) => sheetCharge(code)?.name ?? fallback;

// additional charges the Order Summary owns; saving replaces these and keeps every other one
export const DELIVERY_EXTRA_NAMES = {
  reattempt: chargeName("REATTEMPT_DELIVERY", "Re-attempt Delivery"),
  special: chargeName("SPECIAL_DELIVERY", "Special Delivery"),
  crane: chargeName("CRANE_PULLEY_EQUIPMENT", "Crane / Pulley / Equipment"),
  labour: chargeName("WARAI_UNION_MATHADI", "Warai / Union / Mathadi Charges"),
};

export const isDeliveryExtra = (name?: string | null) => Object.values(DELIVERY_EXTRA_NAMES).some((extra) => sameName(extra, name));

export type DeliveryColumns = Pick<
  IBillingUpdate,
  "appointment" | "sunday_holiday_delivery_charges" | "hard_copy_pod" | "demurrage_charges" | "reattempt_charges" | "crane_charge" | "labour_charge"
>;

export type PricedDeliveryCharges = {
  columns: DeliveryColumns;
  extras: Array<{ charge_name: string; amount: number }>;
  lines: VasLine[];
};

// Prices the delivery charges from the customer's VAS sheet; a charge the customer has no value for stays 0
export const priceDeliveryCharges = (pricing: CustomerPricing, weight: number, freight: number, inputs: DeliveryChargeInputs): PricedDeliveryCharges => {
  const columns: DeliveryColumns = {
    appointment: 0,
    sunday_holiday_delivery_charges: 0,
    hard_copy_pod: 0,
    demurrage_charges: 0,
    reattempt_charges: 0,
    crane_charge: 0,
    labour_charge: 0,
  };
  const extras: PricedDeliveryCharges["extras"] = [];
  const lines: VasLine[] = [];

  if (inputs.specialDelivery) {
    const line = priceSheetCharge("SPECIAL_DELIVERY", pricing, weight, freight);
    const label = SPECIAL_DELIVERY_OPTIONS.find((option) => option.value === inputs.specialDelivery)?.label ?? inputs.specialDelivery;
    if (line) {
      lines.push({ ...line, label: `${line.label} (${label})` });
      if (inputs.specialDelivery === "APPOINTMENT") columns.appointment = line.amount;
      else extras.push({ charge_name: DELIVERY_EXTRA_NAMES.special, amount: line.amount });
    }
  }

  if (inputs.isSundayOrHoliday) {
    const line = priceSheetCharge("SUNDAY_HOLIDAY_DELIVERY", pricing, weight, freight);
    if (line) {
      lines.push(line);
      columns.sunday_holiday_delivery_charges = line.amount;
    }
  }

  const chargedAttempts = Math.max(0, inputs.deliveryAttempts - FREE_DELIVERY_ATTEMPTS);
  if (chargedAttempts > 0) {
    const line = priceSheetCharge("REATTEMPT_DELIVERY", pricing, weight, freight);
    if (line) {
      const amount = round2(line.amount * chargedAttempts);
      lines.push({ ...line, label: `${line.label} × ${chargedAttempts}`, amount });
      columns.reattempt_charges = amount;
    }
  }

  if (inputs.hardCopyPod) {
    const line = priceSheetCharge("HARD_COPY_POD", pricing, weight, freight);
    if (line) {
      lines.push(line);
      columns.hard_copy_pod = line.amount;
    }
  }

  // per kg per day, for the days beyond the free period
  const chargedDays = Math.max(0, inputs.storageDays - FREE_STORAGE_DAYS);
  if (chargedDays > 0) {
    const line = priceSheetCharge("WAREHOUSE_DEMURRAGE", pricing, weight * chargedDays, freight);
    if (line) {
      lines.push({ ...line, label: `${line.label} × ${chargedDays} day${chargedDays === 1 ? "" : "s"}` });
      columns.demurrage_charges = line.amount;
    }
  }

  // crane and labour are as per actual: the amount entered, in their own columns
  if (inputs.craneAmount !== null && inputs.craneAmount > 0) {
    columns.crane_charge = round2(inputs.craneAmount);
    lines.push({ label: DELIVERY_EXTRA_NAMES.crane, amount: columns.crane_charge });
  }
  if (inputs.labourAmount !== null && inputs.labourAmount > 0) {
    columns.labour_charge = round2(inputs.labourAmount);
    lines.push({ label: DELIVERY_EXTRA_NAMES.labour, amount: columns.labour_charge });
  }

  return { columns, extras, lines };
};

// The charges ＋ can add to a bill: its other VAS columns, then the customer's sheet charges that have no column
export type AddableCharge = { key: string; label: string; code?: string; column?: VasColumn };

export const ADDABLE_CHARGES: AddableCharge[] = [
  ...BILL_VAS_COLUMNS.filter(({ column }) => column !== "docket_charge" && column !== "fuel_surcharge").map(({ column, label }) => ({
    key: column,
    label,
    column,
    code: Object.keys(COLUMN_BY_CODE).find((code) => COLUMN_BY_CODE[code] === column),
  })),
  ...SHEET_CHARGES.filter(
    (charge) => !COLUMN_BY_CODE[charge.code] && !FREIGHT_RULE_CODES.includes(charge.code) && !NOT_PRICEABLE_CODES.includes(charge.code)
  ).map((charge) => ({ key: charge.code, label: charge.name, code: charge.code })),
];

// What a bill charges for VAS, for the list's expandable row: every VAS column and additional charge with an amount
export const getBillVasLines = (bill: IBillingListItem): VasLine[] => [
  ...BILL_VAS_COLUMNS.filter(({ column }) => toNumber(bill[column]) !== 0).map(({ column, label }) => ({ label, amount: toNumber(bill[column]) })),
  ...(bill.additional_charges ?? [])
    .filter((extra) => toNumber(extra.amount) !== 0)
    .map((extra) => ({ label: extra.charge_name, amount: toNumber(extra.amount) })),
];

/* ---------- repricing the bills a Generate Billing run created ---------- */

const RUN_POLL_MS = 5000;
const RUN_TIMEOUT_MS = 20 * 60 * 1000;
const LIST_LIMIT = 1000;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const waitForRun = async (runId: number) => {
  const started = Date.now();
  while (Date.now() - started < RUN_TIMEOUT_MS) {
    const { data } = await getBillingBulkRunByIdApi(runId);
    const run: IBillingBulkRun | undefined = data?.data;
    if (run && run.status !== "RUNNING") return run;
    await wait(RUN_POLL_MS);
  }
  return null;
};

// bills matching the run's filters that were created after the run started, i.e. by this run
const findRunBills = async (run: IBillingBulkRun, filters: IGenerateBilling) => {
  const createdSince = new Date(run.createdAt).getTime();
  const isNew = (bill: IBillingListItem) => bill.createdAt && new Date(bill.createdAt).getTime() >= createdSince;

  const fetchPage = async (query: Record<string, string | undefined>) => {
    const { data } = await getBillingListApi({ page: 0, pageLimit: LIST_LIMIT, ...query });
    return (data?.data?.rows ?? []) as IBillingListItem[];
  };

  const rows = filters.awb_numbers?.length
    ? (await Promise.all(filters.awb_numbers.map((awb) => fetchPage({ search: awb, customer_id: filters.customer_id })))).flat()
    : await fetchPage({ from_date: filters.from_date, to_date: filters.to_date, customer_id: filters.customer_id, branch_id: filters.branch_id });

  const unique = new Map(rows.filter(isNew).map((bill) => [bill.id, bill]));
  return Array.from(unique.values());
};

export const repriceGeneratedRun = async (run: IBillingBulkRun, filters: IGenerateBilling) => {
  const toastId = toast.loading("Waiting for billing to finish, then pricing it from customer rates…", { position: "top-right" });
  try {
    const finished = await waitForRun(run.id);
    if (!finished) {
      toast.update(toastId, { render: "Billing is taking long; new bills keep the default pricing for now.", type: "warning", isLoading: false, autoClose: 8000 });
      return;
    }

    const bills = await findRunBills(finished, filters);
    if (!bills.length) {
      toast.update(toastId, { render: "No new bills to price from customer rates.", type: "info", isLoading: false, autoClose: 5000 });
      return;
    }

    clearCustomerPricingCache();
    let done = 0;
    let failed = 0;
    let onMasterRate = 0;
    let odaUnchecked = 0;
    for (const bill of bills) {
      try {
        const { data: awbResponse } = await getAwbByIdApi(String(bill.awb_id));
        const deliveryStatus = await loadDeliveryStatus(awbResponse?.data?.destination_pincode);
        if (deliveryStatus === null) odaUnchecked += 1;
        const priced = priceBill(bill, await loadCustomerPricing(bill.customer_id), toAwbCharges(awbResponse?.data, deliveryStatus));
        if (priced.rateSource !== "customer") onMasterRate += 1;
        await editBillingDataByIdApi({ id: bill.id, data: priced.update, qc: { billing_qc_status: bill.billing_qc_status, billing_qc_remarks: bill.billing_qc_remarks } });
        done += 1;
      } catch (error) {
        console.error(error);
        failed += 1;
      }
      toast.update(toastId, { render: `Pricing new bills from customer rates… ${done + failed}/${bills.length}` });
    }

    const notes = [failed && `${failed} failed`, onMasterRate && `${onMasterRate} had no customer rate for their lane`, odaUnchecked && `${odaUnchecked} couldn't be checked for ODA`].filter(Boolean).join(", ");
    toast.update(toastId, {
      render: `${done} new bill${done === 1 ? "" : "s"} priced from customer rates${notes ? ` (${notes})` : ""}.`,
      type: failed || odaUnchecked ? "warning" : "success",
      isLoading: false,
      autoClose: 8000,
    });

    const lastListQuery = store.reduxStore.getState().master.billing?.lastListQuery;
    if (lastListQuery) store.reduxStore.dispatch(getBillingList(lastListQuery));
  } catch (error) {
    console.error(error);
    toast.update(toastId, { render: "Couldn't price the new bills from customer rates.", type: "error", isLoading: false, autoClose: 8000 });
  }
};
